import { Box, Heading, Table, Tbody, Text } from '@chakra-ui/react'
import { Field, RepeatableSectionTypeDef } from '@micin-jp/chicken-schema'

import { checkVisibleIf } from './checkVisibleIf'
import { PrintField } from './PrintField'
import { FieldValue, RepeatableSectionFieldValue } from '../../types'
import { FlattenFieldMap } from '../../utils/getFlattenFields'

type Props = {
  repeatableSection: Field & { typeDef: RepeatableSectionTypeDef }
  fieldValues: FieldValue[]
  fieldMap: FlattenFieldMap
}

/**
 * リピータブルセクションの有効な行のインデックスを取得
 */
const getEnabledRowIndices = (
  fieldValues: FieldValue[],
  fid: string,
): number[] => {
  return fieldValues
    .filter(
      (fv): fv is RepeatableSectionFieldValue =>
        fv.fid === fid && fv.type === 'repeatableSection' && fv.isEnabled,
    )
    .map(fv => fv.index)
    .sort((a, b) => a - b)
}

export const PrintRepeatableSection: React.FC<Props> = ({
  repeatableSection,
  fieldValues,
  fieldMap,
}) => {
  // visibleIf条件をチェック
  if (!checkVisibleIf(repeatableSection, fieldValues, fieldMap)) {
    return null
  }

  const sectionFields = repeatableSection.typeDef.fields
  const enabledIndices = getEnabledRowIndices(
    fieldValues,
    repeatableSection.fid,
  )

  // 有効な行がない場合は何も表示しない
  if (enabledIndices.length === 0) {
    return null
  }

  return (
    <Box className="print-repeatable-section">
      {/* H2: セクション名 */}
      <Heading
        as="h2"
        fontSize="lg"
        fontWeight="bold"
        mb="2"
        className="print-section-title"
      >
        {repeatableSection.name}
      </Heading>

      {/* 各行を表示 */}
      {enabledIndices.map((rowIndex, displayIndex) => (
        <Box key={rowIndex} mb="4" className="print-repeatable-row">
          <Text fontWeight="medium" mb="1" color="gray.700">
            # {displayIndex + 1}
          </Text>
          <Table
            variant="unstyled"
            className="print-field-table"
            sx={{
              borderCollapse: 'collapse',
              width: '100%',
              border: '2px solid black',
            }}
          >
            <Tbody>
              {sectionFields.map(field => (
                <PrintField
                  key={`${field.fid}-${rowIndex}`}
                  field={field}
                  fieldValues={fieldValues}
                  fieldMap={fieldMap}
                  index={rowIndex}
                />
              ))}
            </Tbody>
          </Table>
        </Box>
      ))}
    </Box>
  )
}
